/**
 * Die Befehlsliste hinter Cmd+K.
 *
 * WOZU. Die Leiste wächst mit jeder Funktion, und irgendwann ist sie wieder
 * voll — vier Anläufe an einem Tag (19.08.2026) haben gezeigt, wie schnell das
 * geht. Sanity, Contentful, Linear und Notion lösen das seit Jahren gleich: Was
 * man selten braucht, bekommt keinen Knopf, sondern einen Eintrag in einer
 * Suche, die überall auf demselben Tastenkürzel liegt.
 *
 * DIE REGEL FÜR ALLES NEUE: erst hier eintragen, dann überlegen, ob es
 * zusätzlich einen Knopf verdient. Ein Eintrag kostet nichts und nimmt niemandem
 * Platz weg.
 *
 * WIE ES ZUSAMMENKOMMT. Jede Ansicht meldet ihre eigenen Befehle an, solange sie
 * offen ist, und nimmt sie beim Schließen wieder mit. Nur sie weiß, was sie
 * gerade anbieten kann: Die Editor-Ansicht kennt ihre Seiten und Felder, die
 * Projektliste kennt die Projekte. Angemeldet wird eine FUNKTION, keine fertige
 * Liste — so bleiben die Einträge aktuell, wenn sich die Seite ändert.
 */
import { computed, onScopeDispose, ref, type ComputedRef } from 'vue'

export interface Befehl {
  /** Eindeutig über alle Quellen hinweg. Dient auch als Schlüssel in der Liste. */
  id: string
  label: string
  /** Überschrift, unter der er in der Liste steht: „Seiten", „Felder", … */
  gruppe: string
  /** Kurze Erklärung rechts. Weglassen, wenn die Beschriftung reicht. */
  hinweis?: string
  tun: () => void
}

type Quelle = () => Befehl[]

/* Anwendungsweit, nicht je Bauteil: Die Palette hängt in App.vue, angemeldet
   wird aus jeder Ansicht. */
const quellen = ref(new Map<string, Quelle>())
const offen = ref(false)

export interface Befehlsliste {
  alle: ComputedRef<Befehl[]>
  offen: typeof offen
  registriere: (id: string, quelle: Quelle) => void
  oeffnen: () => void
  schliessen: () => void
}

export function useBefehle(): Befehlsliste {
  const alle = computed<Befehl[]>(
    () => [...quellen.value.values()].flatMap((quelle) => quelle()),
  )

  /**
   * Befehle anmelden. Verschwindet die Ansicht, verschwinden ihre Befehle —
   * sonst böte die Suche irgendwann Seiten eines Projekts an, das gar nicht
   * mehr offen ist.
   */
  function registriere(id: string, quelle: Quelle): void {
    quellen.value.set(id, quelle)
    onScopeDispose(() => {
      quellen.value.delete(id)
    })
  }

  return {
    alle,
    offen,
    registriere,
    oeffnen: () => { offen.value = true },
    schliessen: () => { offen.value = false },
  }
}
